/**
 * The networked dungeon run, client side (E4.6.S3 slice 2d, AD-704) — the pure
 * half: which advertised entrance the player stands at, how a refusal reads,
 * and how the client rebuilds the run the server just started.
 *
 * THE SERVER CHOSE EVERYTHING. The ack advertises each entrance with the reach
 * the server measures (`DungeonSiteWire`); the `dungeonResult entered` reply
 * discloses the seed, party size and theme it committed to. From those plus the
 * site's difficulty/tier, `generateDungeon` over the ONE bundled catalogue gives
 * the very plan the server built, and `buildLayoutCollision` — the one wall
 * walk in `@great-cloud/shared` (AD-698) — gives the walls the server's
 * instance zone collides against. Prediction then clamps to the disclosed
 * bounds and resolves against those walls, so the client agrees with the
 * server by construction (AD-026). `fingerprint` is the check that the copy is
 * the same plan; a mismatch is reported, never papered over. (The toast words
 * live beside shrine travel's, `ui/panels/dungeonEntry.ts`.)
 *
 * No `three` import: the drawing of the interior is the layer's (slice 2e).
 */

import {
  buildLayoutCollision,
  type DungeonResultPayload,
  type EntityDelta,
  type DungeonSiteWire,
  type WorldCollision,
  type ZoneBounds,
} from '@great-cloud/shared/client';
import {
  DEFAULT_DUNGEON_ENTRANCE_RADII,
  generateDungeon,
  planDungeonEntranceScene,
  stableFingerprint,
  type DungeonCatalog,
  type DungeonEntranceAnchor,
  type DungeonEntranceScenePlan,
  type DungeonEntranceSite,
  type DungeonPlan,
  type DungeonShrineProfile,
} from '@great-cloud/procedural-dungeons';

export type DungeonEntered = Extract<DungeonResultPayload, { ok: true; action: 'entered' }>;

/** The advertised entrance whose reach contains (x, z), nearest first; null when none. */
export function dungeonSiteInReach(
  sites: readonly DungeonSiteWire[] | undefined,
  x: number,
  z: number,
): DungeonSiteWire | null {
  let best: DungeonSiteWire | null = null;
  let bestD = Infinity;
  for (const site of sites ?? []) {
    const d = Math.hypot(x - site.x, z - site.z);
    if (d <= site.reach && d < bestD) {
      best = site;
      bestD = d;
    }
  }
  return best;
}

/**
 * Techdemo sites a server-advertised entrance supersedes: an authored site
 * reuses an anchor's cleared ground (DR-025 — `southroot-hollow` stands on
 * `reach-south-root`), so the client-local preview shrine there would answer
 * the same G as the real entrance. The real one wins; the preview site there
 * is not placed (`layer.ts`).
 */
export function withoutServerSites<T extends { position: { x: number; z: number } }>(
  sites: readonly T[],
  serverSites: readonly { x: number; z: number; reach: number }[],
): T[] {
  return sites.filter(
    (site) =>
      !serverSites.some((s) => Math.hypot(site.position.x - s.x, site.position.z - s.z) <= s.reach),
  );
}

/** A server entrance's shrine: the site it stands on, its profile and its scene plan. */
export interface ServerEntranceShrine {
  site: DungeonEntranceSite;
  profile: DungeonShrineProfile;
  plan: DungeonEntranceScenePlan;
}

/**
 * The shrine a server-advertised entrance shows from a distance
 * (`q-dungeon-server-entrance-has-no-shrine`). Presentation only: the server
 * still decides entry by its own reach. It stands on the authored anchor whose
 * cleared ground it reuses (DR-025: `southroot-hollow` on `reach-south-root`),
 * taking that anchor's facing and height, else on the bare advertised point.
 * Its interaction radius (the gold ring's fade) is the server's reach. The portal reads READY because the entrance
 * is always enterable; no omen is shown, because the server discloses no plan
 * before entry. Null when no shrine profile matches the site's difficulty or
 * the planner refuses the site.
 */
export function serverEntranceShrine(
  wire: DungeonSiteWire,
  zoneId: string,
  anchors: readonly DungeonEntranceAnchor[],
  profiles: readonly DungeonShrineProfile[],
): ServerEntranceShrine | null {
  const profile = profiles.find((entry) => entry.difficultyId === wire.difficulty);
  if (!profile) return null;
  let anchor: DungeonEntranceAnchor | null = null;
  let bestD = Infinity;
  for (const entry of anchors) {
    const d = Math.hypot(entry.position.x - wire.x, entry.position.z - wire.z);
    if (d <= wire.reach && d < bestD) {
      anchor = entry;
      bestD = d;
    }
  }
  const defaults = DEFAULT_DUNGEON_ENTRANCE_RADII;
  const previewRevealRadius = Math.max(defaults.previewRevealRadius, wire.reach * 2);
  const generationRadius = Math.max(defaults.generationRadius, previewRevealRadius * 1.5);
  const material = {
    anchorId: anchor?.id ?? wire.id,
    biomeId: anchor?.biomeId ?? zoneId,
    position: { x: wire.x, y: anchor?.position.y ?? 0, z: wire.z },
    rotationQuarterTurns: anchor?.rotationQuarterTurns ?? 0,
    clearanceRadius: anchor?.clearanceRadius ?? profile.footprintRadius,
    targetTier: wire.tier,
    difficultyId: wire.difficulty,
    // The server knows the theme; the wire does not carry it, and the shrine needs none.
    themeIds: anchor ? [...anchor.themeIds].sort() : ['undisclosed'],
    radii: {
      interactionRadius: wire.reach,
      previewRevealRadius,
      generationRadius,
      resetRadius: Math.max(defaults.resetRadius, generationRadius * (4 / 3)),
    },
  };
  const site: DungeonEntranceSite = {
    id: `dungeon-entrance-${wire.id}`,
    ...material,
    placementFingerprint: stableFingerprint(material),
  };
  try {
    const plan = planDungeonEntranceScene(site, profile, {
      phase: 'dormant',
      siteId: site.id,
      generationCycle: 0,
    });
    return { site, profile, plan: { ...plan, portalState: 'ready' } };
  } catch {
    return null;
  }
}

export interface RebuiltRun {
  plan: DungeonPlan;
  /** The walls the server's instance zone collides against — the predictor's collider. */
  collision: WorldCollision;
  bounds: ZoneBounds;
  /**
   * True only when the server sent a fingerprint and the copy carries the same
   * one. No fingerprint to check is NOT a match (E4.6 feature gate F-M, AD-731):
   * an unverified copy must not lend its walls to the predictor, exactly as a
   * differing one does not (`runClient.ts`).
   */
  matches: boolean;
}

/**
 * Rebuild the run the server started, from what it disclosed. Null when it
 * cannot be rebuilt here: no theme disclosed, or the generator refuses the
 * inputs against this client's catalogue (a stale bundle) — the caller then
 * stays on the server's bounds alone and reports it.
 */
export function rebuildDungeonRun(
  entered: DungeonEntered,
  site: Pick<DungeonSiteWire, 'difficulty' | 'tier'>,
  catalog: DungeonCatalog,
): RebuiltRun | null {
  if (entered.themeId === undefined) return null;
  let plan: DungeonPlan;
  try {
    plan = generateDungeon(
      {
        seed: entered.seed,
        difficultyId: site.difficulty,
        themeId: entered.themeId,
        targetTier: site.tier,
        partySize: entered.partySize,
      },
      catalog,
    );
  } catch {
    return null;
  }
  // A plan without a layout is schema v1 — the server refuses to run one (AD-698).
  if (!plan.layout) return null;
  return {
    plan,
    collision: {
      shapes: buildLayoutCollision({ connections: plan.connections, layout: plan.layout }),
    },
    bounds: entered.bounds,
    matches: entered.fingerprint !== undefined && entered.fingerprint === plan.fingerprint,
  };
}

/**
 * Our body in a full snapshot after a run began or ended (AD-705). The new zone
 * allocates its own entity ids and sends no ack, so the id is found by the
 * character's name — unique across characters (case-insensitive index), and
 * echoed verbatim by the server — on a `player` entity. Null when the frame does
 * not carry it (or we have no name yet). A full snapshot carries full states.
 */
export function ownBodyIn(entities: readonly EntityDelta[], ownName: string | null): number | null {
  if (ownName === null) return null;
  for (const e of entities) {
    if (e.kind === 'player' && e.name === ownName) return e.id;
  }
  return null;
}
